import { useEffect, useState, useRef } from 'react';
import { ppeItemsAPI, ppeIssuesAPI } from '../../lib/api';
import { formatDate, cn } from '../../lib/utils';
import { HardHat, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { PPEAcknowledgementBlock } from './PPEAcknowledgementBlock';

const issueReasons = [
  'New issue',
  'Replacement',
  'Damaged',
  'Lost',
  'Worn out',
  'Additional issue'
];

const conditions = [
  'New',
  'Good',
  'Fair'
];

export function PPEIssueModal({ open, onClose, employee, onSuccess }) {
  const [ppeItems, setPpeItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [acknowledgementType, setAcknowledgementType] = useState('issuer_confirmation');
  const [signatureData, setSignatureData] = useState(null);
  const [typedAck, setTypedAck] = useState('');
  const [issuerConfirmed, setIssuerConfirmed] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  const [formData, setFormData] = useState({
    issue_date: new Date().toISOString().split('T')[0],
    ppe_item_id: '',
    quantity: 1,
    size: '',
    unit_of_issue: '',
    reason_for_issue: '',
    expected_replacement_date: '',
    condition_when_issued: 'New',
    serial_batch_number: '',
    notes: '',
  });

  useEffect(() => {
    if (open) {
      fetchPPEItems();
      // Reset form
      setFormData({
        issue_date: new Date().toISOString().split('T')[0],
        ppe_item_id: '',
        quantity: 1,
        size: '',
        unit_of_issue: '',
        reason_for_issue: '',
        expected_replacement_date: '',
        condition_when_issued: 'New',
        serial_batch_number: '',
        notes: '',
      });
      setSelectedItem(null);
      setAcknowledgementType('issuer_confirmation');
      setSignatureData(null);
      setTypedAck('');
      setIssuerConfirmed(false);
    }
  }, [open]);

  const fetchPPEItems = async () => {
    try {
      const res = await ppeItemsAPI.getAll({ active_only: true });
      setPpeItems(res.data);
    } catch (error) {
      toast.error('Failed to load PPE items');
    } finally {
      setLoading(false);
    }
  };

  const handleItemSelect = (itemId) => {
    const item = ppeItems.find(i => i.id === itemId);
    setSelectedItem(item);
    setFormData(prev => ({
      ...prev,
      ppe_item_id: itemId,
      unit_of_issue: item?.units?.[0] || '',
      size: '',
      expected_replacement_date: item?.default_replacement_days 
        ? new Date(Date.now() + item.default_replacement_days * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        : ''
    }));
  };

  // Canvas drawing functions for signature
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && canvasRef.current) {
      setSignatureData(canvasRef.current.toDataURL());
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
  };

  const handleSubmit = async () => {
    if (!formData.ppe_item_id || !formData.reason_for_issue || !formData.unit_of_issue) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate acknowledgement
    let ackData = null;
    if (acknowledgementType === 'signature' && !signatureData) {
      toast.error('Please capture employee signature');
      return;
    }
    if (acknowledgementType === 'typed' && !typedAck.trim()) {
      toast.error('Please enter typed acknowledgement');
      return;
    }
    if (acknowledgementType === 'issuer_confirmation' && !issuerConfirmed) {
      toast.error('Please confirm the issue');
      return;
    }

    if (acknowledgementType === 'signature') {
      ackData = signatureData;
    } else if (acknowledgementType === 'typed') {
      ackData = typedAck;
    } else {
      ackData = 'Issuer confirmed on behalf of employee';
    }

    setSaving(true);
    try {
      await ppeIssuesAPI.create({
        ...formData,
        employee_id: employee.employee_id,
        acknowledgement_type: acknowledgementType,
        acknowledgement_data: ackData,
      });
      toast.success('PPE issued successfully');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue PPE');
    } finally {
      setSaving(false);
    }
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HardHat className="w-5 h-5 text-primary" />
            Issue PPE
          </DialogTitle>
          <DialogDescription>
            Issue personal protective equipment to {employee.full_name}
          </DialogDescription>
        </DialogHeader>

        {/* Employee Info Banner */}
        <div className="p-4 rounded-lg bg-secondary/30 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-muted-foreground">Employee</p>
            <p className="font-medium">{employee.full_name}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Employee #</p>
            <p className="font-medium">{employee.employee_id}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Department</p>
            <p className="font-medium">{employee.department}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Job Title</p>
            <p className="font-medium">{employee.job_title}</p>
          </div>
        </div>

        <div className="space-y-4 py-2">
          {/* Issue Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Issue Date *</Label>
              <Input
                type="date"
                value={formData.issue_date}
                onChange={(e) => setFormData({...formData, issue_date: e.target.value})}
                data-testid="input-ppe-issue-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Serial/Batch Number</Label>
              <Input
                value={formData.serial_batch_number}
                onChange={(e) => setFormData({...formData, serial_batch_number: e.target.value})}
                placeholder="Optional"
                data-testid="input-ppe-serial"
              />
            </div>
          </div>

          {/* PPE Item Selection */}
          <div className="space-y-2">
            <Label>PPE Item *</Label>
            <Select value={formData.ppe_item_id} onValueChange={handleItemSelect}>
              <SelectTrigger data-testid="select-ppe-item">
                <SelectValue placeholder="Select PPE item" />
              </SelectTrigger>
              <SelectContent>
                {ppeItems.length === 0 ? (
                  <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                    No PPE items available.<br />
                    Add items in <strong>PPE Management</strong> first.
                  </div>
                ) : (
                  ppeItems.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} ({item.category})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Size, Quantity, Unit */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Size</Label>
              <Select 
                value={formData.size} 
                onValueChange={(v) => setFormData({...formData, size: v})}
                disabled={!selectedItem?.sizes_available?.length}
              >
                <SelectTrigger data-testid="select-ppe-size">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  {selectedItem?.sizes_available?.map((size) => (
                    <SelectItem key={size} value={size}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Quantity *</Label>
              <Input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 1})}
                data-testid="input-ppe-quantity"
              />
            </div>
            <div className="space-y-2">
              <Label>Unit *</Label>
              <Select 
                value={formData.unit_of_issue} 
                onValueChange={(v) => setFormData({...formData, unit_of_issue: v})}
              >
                <SelectTrigger data-testid="select-ppe-unit">
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  {(selectedItem?.units || ['pieces', 'pairs', 'sets', 'boxes']).map((unit) => (
                    <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Reason and Condition */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Reason for Issue *</Label>
              <Select 
                value={formData.reason_for_issue} 
                onValueChange={(v) => setFormData({...formData, reason_for_issue: v})}
              >
                <SelectTrigger data-testid="select-ppe-reason">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {issueReasons.map((reason) => (
                    <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Condition When Issued *</Label>
              <Select 
                value={formData.condition_when_issued} 
                onValueChange={(v) => setFormData({...formData, condition_when_issued: v})}
              >
                <SelectTrigger data-testid="select-ppe-condition">
                  <SelectValue placeholder="Select condition" />
                </SelectTrigger>
                <SelectContent>
                  {conditions.map((cond) => (
                    <SelectItem key={cond} value={cond}>{cond}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Replacement Date */}
          <div className="space-y-2">
            <Label>Expected Replacement Date</Label>
            <Input
              type="date"
              value={formData.expected_replacement_date}
              onChange={(e) => setFormData({...formData, expected_replacement_date: e.target.value})}
              data-testid="input-ppe-replacement-date"
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notes/Comments</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Any additional notes..."
              rows={2}
              data-testid="input-ppe-notes"
            />
          </div>

          {/* Acknowledgement Section */}
          <PPEAcknowledgementBlock
            acknowledgementType={acknowledgementType}
            onChangeType={setAcknowledgementType}
            signatureCanvasRef={canvasRef}
            onStartDrawing={startDrawing}
            onDraw={draw}
            onStopDrawing={stopDrawing}
            onClearSignature={clearSignature}
            typedAck={typedAck}
            onChangeTypedAck={setTypedAck}
            issuerConfirmed={issuerConfirmed}
            onChangeIssuerConfirmed={setIssuerConfirmed}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="submit-ppe-issue-btn">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Issuing...
              </>
            ) : (
              'Issue PPE'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
